import { useClients } from '@services/users/queries/useClients'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ComboboxOption } from '@/components/ui/combobox'
import { MultiCombobox } from '@/components/ui/multi-combobox'

interface Props {
  value: string[]
  onChange: (value: string[]) => void
  /** Already-attached landlords, used to seed chip labels (email) before search. */
  initialLandlords?: Array<{ id: string; email?: string } | undefined>
  invalid?: boolean
}

export function FlatLandlordsField({
  value,
  onChange,
  initialLandlords = [],
  invalid
}: Props) {
  const { t } = useTranslation('flat')
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [options, setOptions] = useState<ComboboxOption[]>([])
  const seededKey = useRef('')

  // Seed labels for the landlords already attached to the flat so their chips
  // render an email instead of a raw id before any search runs.
  useEffect(() => {
    const seed = initialLandlords
      .filter((l): l is { id: string; email?: string } => !!l?.id && !!l?.email)
      .map((l) => ({ value: l.id, label: l.email! }))
    const key = seed.map((o) => o.value).join(',')
    if (!seed.length || key === seededKey.current) return
    seededKey.current = key
    setOptions((prev) => {
      const ids = new Set(prev.map((o) => o.value))
      const fresh = seed.filter((o) => !ids.has(o.value))
      return fresh.length ? [...fresh, ...prev] : prev
    })
  }, [initialLandlords])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchValue), 300)
    return () => clearTimeout(timer)
  }, [searchValue])

  const query = useClients({
    params: {
      roles: 'LANDLORD',
      search: debouncedSearch,
      limit: 20,
      offset: '0'
    }
  })

  // Accumulate fetched landlords into the option list (never drop already-seen).
  useEffect(() => {
    const results = query.data?.results
    if (!results) return
    const fetched = results
      .filter((u) => u.email && u.id)
      .map((u) => ({ value: u.id!, label: u.email! }))
    setOptions((prev) => {
      const ids = new Set(prev.map((o) => o.value))
      const fresh = fetched.filter((o) => !ids.has(o.value))
      return fresh.length ? [...prev, ...fresh] : prev
    })
  }, [query.data])

  return (
    <MultiCombobox
      value={value}
      onChange={onChange}
      onSearch={setSearchValue}
      options={options}
      loading={query.isFetching}
      invalid={invalid}
      placeholder={t('form.labels.landlord')}
    />
  )
}
